/**
 * Public list of subprocessors that receive personal data on Tune4Tune's
 * behalf. Referenced from the Privacy Policy — keep this in sync with the
 * `processors_that_may_hold_related_data` block in /app/backend/routes/privacy.py
 * so exports and public docs never drift.
 */
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const CONTACT_EMAIL = 'hallandpull@gmail.com';
const LAST_UPDATED = 'February 2026';

const PROCESSORS = [
  {
    name: 'Stripe, Inc.',
    purpose: 'Card payments, artist payouts, subscription billing.',
    location: 'United States',
    data: 'Cardholder name, last-4 of card, billing address, IP at payment, Stripe customer + payment intent IDs.',
    safeguards: 'EU–US Data Privacy Framework certified; PCI-DSS Level 1.',
    privacyUrl: 'https://stripe.com/privacy',
    dpaUrl: 'https://stripe.com/legal/dpa',
  },
  {
    name: 'Resend',
    purpose: 'Transactional email (account verification, password reset, order updates, receipts).',
    location: 'United States',
    data: 'Recipient email, subject, body content of the transactional email sent.',
    safeguards: 'DPA available on request; email content encrypted in transit.',
    privacyUrl: 'https://resend.com/legal/privacy-policy',
    dpaUrl: 'https://resend.com/legal/dpa',
  },
  {
    name: 'MongoDB Atlas (MongoDB, Inc.)',
    purpose: 'Managed database hosting for user, order, artist, and studio-export records.',
    location: 'United States (primary), automatic backups in the same region.',
    data: 'Everything stored on the platform except payment card numbers and uploaded media (see Emergent Object Storage).',
    safeguards: 'AES-256 encryption at rest, TLS in transit, SOC 2 Type II + ISO 27001 audited.',
    privacyUrl: 'https://www.mongodb.com/legal/privacy-policy',
    dpaUrl: 'https://www.mongodb.com/legal/dpa',
  },
  {
    name: 'Emergent',
    purpose: 'Application hosting, object storage for uploaded audio and delivered dubplates, LLM-assisted moderation.',
    location: 'United States',
    data: 'All uploaded audio (instrumentals, vocal takes, mixdowns), delivered files, thumbnails.',
    safeguards: 'Encryption at rest and in transit; access limited to authenticated Tune4Tune requests.',
    privacyUrl: 'https://emergent.sh/legal/privacy',
    dpaUrl: null,
  },
];

function Row({ p }) {
  return (
    <div className="border border-[#222] rounded-xl p-5 bg-[#0e0e0e]" data-testid={`subprocessor-${p.name.split(',')[0].toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="font-heading font-bold text-white text-lg">{p.name}</h2>
          <p className="text-xs text-[#666] mt-0.5">Processing location: {p.location}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0 text-xs">
          <a href={p.privacyUrl} target="_blank" rel="noreferrer" className="text-lime hover:underline inline-flex items-center gap-1">
            Privacy policy <ExternalLink className="w-3 h-3" />
          </a>
          {p.dpaUrl && (
            <a href={p.dpaUrl} target="_blank" rel="noreferrer" className="text-lime hover:underline inline-flex items-center gap-1">
              DPA <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div>
          <dt className="uppercase tracking-wider text-[#666] text-[10px] mb-1">Purpose</dt>
          <dd className="text-[#D4D4D8]">{p.purpose}</dd>
        </div>
        <div>
          <dt className="uppercase tracking-wider text-[#666] text-[10px] mb-1">Data types</dt>
          <dd className="text-[#D4D4D8]">{p.data}</dd>
        </div>
        <div>
          <dt className="uppercase tracking-wider text-[#666] text-[10px] mb-1">Safeguards</dt>
          <dd className="text-[#D4D4D8]">{p.safeguards}</dd>
        </div>
      </dl>
    </div>
  );
}

export default function Subprocessors() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-24 space-y-8" data-testid="subprocessors-page">
        <header className="space-y-2">
          <p className="text-lime text-xs uppercase tracking-widest font-semibold">Legal</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Subprocessors</h1>
          <p className="text-[#A1A1AA] text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-sm text-[#D4D4D8] leading-relaxed max-w-2xl">
            These are the third-party vendors that receive personal data on
            Tune4Tune's behalf to help us run the service. We share the minimum
            necessary and only for the specific purpose listed. See our{' '}
            <Link to="/privacy" className="text-lime hover:underline">Privacy Policy</Link>{' '}
            for the full context.
          </p>
        </header>

        <div className="space-y-3">
          {PROCESSORS.map((p) => <Row key={p.name} p={p} />)}
        </div>

        <div className="text-xs text-[#666] leading-relaxed pt-6 border-t border-[#1a1a1a]">
          <p>
            <strong className="text-[#A1A1AA]">Notifications of changes.</strong>{' '}
            We update this page whenever we add, remove, or replace a subprocessor. Enterprise customers with a signed DPA receive at least 30 days' advance notice by email — email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-lime hover:underline">{CONTACT_EMAIL}</a>{' '}
            to be added to that list.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
